import * as React from "react"
import * as ProgressPrimitive from "@radix-ui/react-progress"

import { cn } from "@/lib/utils"

interface ProgressProps
  extends React.ComponentPropsWithoutRef<typeof ProgressPrimitive.Root> {
  /** Merged onto the moving bar, so callers stop reaching in with `[&>div]:`. */
  indicatorClassName?: string
  /**
   * The total is not known yet: the bar slides with the app's loading-bar
   * keyframe instead of filling. Reduced motion pins it at a 40% rest
   * (index.css), never full width, so it cannot read as finished.
   */
  indeterminate?: boolean
}

/** Radix rejects (and logs) a value outside 0..100; an over-full count also
 *  pushed the bar right of its track. */
function clampPercent(value: number | null | undefined): number | null {
  if (value == null || Number.isNaN(value)) return null
  return Math.min(100, Math.max(0, value))
}

const Progress = React.forwardRef<
  React.ElementRef<typeof ProgressPrimitive.Root>,
  ProgressProps
>(({ className, value, indicatorClassName, indeterminate = false, ...props }, ref) => {
  const percent = indeterminate ? null : clampPercent(value)
  return (
    <ProgressPrimitive.Root
      ref={ref}
      className={cn(
        "relative h-4 w-full overflow-hidden rounded-full bg-secondary",
        className
      )}
      value={percent}
      {...props}
    >
      <ProgressPrimitive.Indicator
        className={cn(
          "h-full w-full flex-1 bg-primary transition-transform motion-reduce:transition-none",
          indeterminate && "animate-loading-bar",
          indicatorClassName
        )}
        // The keyframe owns transform while indeterminate; an inline transform
        // would beat the reduced-motion rest rule in index.css.
        style={indeterminate ? undefined : { transform: `translateX(-${100 - (percent ?? 0)}%)` }}
      />
    </ProgressPrimitive.Root>
  )
})
Progress.displayName = ProgressPrimitive.Root.displayName

export { Progress }
